import React, { useEffect, useMemo, useRef, useState } from 'react'
import Check from 'components/Form/Check'
import Input from 'components/Form/Input'
import CompanyAutocomplete from 'components/StaffEssentialsInvites/CompanyAutocomplete'
import useI18n from 'hooks/useI18n'
import useSalesforceReview from './useSalesforceReview'

export default function useManualApproval({ row, loading, loadCompanyOptions, loadIdentityReview, onClose, onConfirm }) {
  const { t } = useI18n()
  const translate = useRef(t)
  translate.current = t
  const [useRegistrationCompany, setUseRegistrationCompany] = useState(false)
  const [editCompanyName, setEditCompanyName] = useState(false)
  const [companyName, setCompanyName] = useState(row?.companyName || '')
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState(null)
  const [companyLoading, setCompanyLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const confirmationInFlight = useRef(false)
  const salesforce = useSalesforceReview(row, loadIdentityReview, row?.approvalFlow === 'partner' ? companyName : undefined)

  const selectedReviewAccount = salesforce.selectedAccount
  useEffect(() => {
    if (salesforce.review?.version === 2 && selectedReviewAccount) {
      setSelected({ ...selectedReviewAccount, salesforceAccountId: selectedReviewAccount.id })
      setQuery(selectedReviewAccount.name)
    }
  }, [salesforce.review?.version, selectedReviewAccount])

  useEffect(() => {
    let active = true
    if (!loadCompanyOptions || useRegistrationCompany || row?.approvalFlow === 'partner' || (salesforce.review?.requiresSelection && salesforce.review?.version !== 2)) {
      setCompanyLoading(false)
      return undefined
    }
    setOptions([])
    setCompanyLoading(true)
    const timeout = window.setTimeout(async () => {
      let result = null
      try {
        result = await loadCompanyOptions({ query, page: 1, pageSize: 30 })
      } catch {
        result = { error: true }
      }
      if (!active) return
      if (!result || result?.error) {
        setOptions([])
        setError(result?.message || translate.current('user_approvals_company_load_error'))
      } else {
        setOptions(Array.isArray(result?.data) ? result.data : [])
        setError('')
      }
      setCompanyLoading(false)
    }, 300)
    return () => {
      active = false
      window.clearTimeout(timeout)
    }
  }, [loadCompanyOptions, query, useRegistrationCompany, row?.approvalFlow, salesforce.review?.requiresSelection, salesforce.review?.version])

  const canConfirmReviewedSelection = () => Boolean(salesforce.selectedAccount?.selectable
    && (salesforce.contactId
      ? salesforce.selectedContact?.selectable
        && (salesforce.selectedContact.exactEmail || salesforce.reason.trim())
        && ((salesforce.selectedContact.accountId || null) === salesforce.accountId || salesforce.allowAccountChange)
      : salesforce.review?.canCreateContact
        && (!salesforce.review.needsPersonDecision || (salesforce.createNew && salesforce.reason.trim()))))

  const submitConfirmation = async () => {
    if (loading || salesforce.reviewLoading || salesforce.reviewError) return
    if (salesforce.review?.version === 2 && !salesforce.review.partnerFlow) {
      if (!canConfirmReviewedSelection()) return
      const succeeded = await onConfirm({ salesforceReview: {
        action: salesforce.contactId ? 'reuse_contact' : 'create_contact',
        ...(salesforce.contactId ? { contactId: salesforce.contactId } : {}),
        accountId: salesforce.accountId, selectedAccountId: salesforce.review.selectedAccountId,
        fingerprint: salesforce.review.fingerprint, allowAccountChange: salesforce.allowAccountChange,
        reason: salesforce.reason.trim(),
      } })
      if (succeeded === true) onClose()
      else salesforce.reloadReview()
      return
    }
    if (salesforce.review?.requiresSelection) {
      if (!salesforce.selectedContact?.selectable || (salesforce.review.accountRequired && !salesforce.selectedAccount?.selectable)
        || (salesforce.accountId && !salesforce.selectedAccount?.selectable)
        || ((salesforce.selectedContact.accountId || null) !== (salesforce.accountId || salesforce.selectedContact.accountId || null) && !salesforce.allowAccountChange)) return
      const succeeded = await onConfirm({ salesforceReview: { contactId: salesforce.contactId,
        accountId: salesforce.accountId || null, fingerprint: salesforce.review.fingerprint, allowAccountChange: salesforce.allowAccountChange } })
      if (succeeded) onClose()
      else salesforce.reloadReview()
      return
    }
    if (row?.approvalFlow === 'partner') {
      if (loading || (loadIdentityReview && !companyName.trim()) || (salesforce.review?.partnerPlan?.accountChoiceRequired && !salesforce.selectedAccount?.selectable)) return
      const succeeded = await onConfirm({ ...(editCompanyName ? { editCompanyName: true, companyName } : {}),
        ...(salesforce.review?.partnerPlan?.accountChoiceRequired ? { partnerAccountId: salesforce.accountId } : {}) })
      if (succeeded) onClose()
      else if (loadIdentityReview) salesforce.reloadReview()
      return
    }
    if (loading || (!useRegistrationCompany && !selected?.id)) return
    if (useRegistrationCompany && row?.canApproveWithRegisteredCompany !== true) return
    const succeeded = await onConfirm(useRegistrationCompany
      ? { useRegistrationCompany: true }
      : { companyId: selected.id })
    if (succeeded) onClose()
    else if (loadIdentityReview) salesforce.reloadReview()
  }


  const handleConfirm = async () => {
    if (confirmationInFlight.current) return
    confirmationInFlight.current = true
    setSubmitting(true)
    try {
      return await submitConfirmation()
    } finally {
      confirmationInFlight.current = false
      setSubmitting(false)
    }
  }

  const formItems = useMemo(() => row?.approvalFlow === 'partner' ? [
    ...(row?.canEditCompanyName ? [{
      full: true,
      custom: <Check label={t('user_approvals_edit_company_name')} checked={editCompanyName}
        disabled={loading || submitting} onChange={setEditCompanyName} />,
    }] : []),
    {
      full: true,
      custom: <Input id="approval-company-name" placeholder={t('user_approvals_company')}
        value={companyName} disabled={!editCompanyName || loading || submitting || !row?.canEditCompanyName}
        onChange={event => { setCompanyName(event.target.value); salesforce.setAccountId('') }} />,
    },
  ] : [
    ...(row?.canApproveWithRegisteredCompany === true ? [{
      full: true,
      custom: <Check
        label={t('user_approvals_use_registered_company')}
        checked={useRegistrationCompany}
        disabled={loading || submitting}
        onChange={checked => {
          setUseRegistrationCompany(checked)
          setSelected(null)
          setQuery('')
          setError('')
        }}
      />,
    }] : []),
    ...(!useRegistrationCompany ? [{
      full: true,
      custom: <CompanyAutocomplete
        placeholder={t('user_approvals_customer_select')}
        inputPlaceholder={t('user_approvals_customer_search_placeholder')}
        options={options}
        showAccountDetails={salesforce.review?.version === 2}
        value={selected}
        inputValue={query}
        loading={companyLoading}
        noOptionsText={t(query ? 'user_approvals_no_companies' : 'user_approvals_search_companies')}
        onInputChange={nextQuery => {
          setOptions([])
          setError('')
          setSelected(null)
          setQuery(nextQuery)
          if (salesforce.review?.version === 2 && salesforce.accountId) salesforce.setAccountId('')
        }}
        onChange={company => {
          if (loading || submitting) return
          setSelected(company)
          setQuery(company?.name || '')
          if (salesforce.review?.version === 2) salesforce.setAccountId(company?.salesforceAccountId || '')
        }}
      />,
    }] : []),
  ], [row?.approvalFlow, row?.canEditCompanyName, row?.canApproveWithRegisteredCompany, editCompanyName, companyName, useRegistrationCompany, loading, submitting, options, selected, query, companyLoading, t, salesforce])

  return { t, formItems, selected, useRegistrationCompany, error, handleConfirm, salesforce, submitting,
    confirmationDisabled: loading || submitting || salesforce.reviewLoading || !!salesforce.reviewError || (salesforce.review?.version === 2 && !salesforce.review.partnerFlow
      ? !canConfirmReviewedSelection() : salesforce.review?.requiresSelection
      ? !salesforce.selectedContact?.selectable || (salesforce.review.accountRequired && !salesforce.selectedAccount?.selectable)
        || ((salesforce.selectedContact?.accountId || null) !== (salesforce.accountId || salesforce.selectedContact?.accountId || null) && !salesforce.allowAccountChange)
      : row?.approvalFlow === 'partner'
        ? (loadIdentityReview && !companyName.trim()) || (salesforce.review?.partnerPlan?.accountChoiceRequired && !salesforce.selectedAccount?.selectable)
        : !useRegistrationCompany && !selected?.id) }
}
